/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, merge, Subject, take, takeUntil } from 'rxjs';

import { emailValidator, noWhitespaceValidator } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  existingAppMemberValidator,
  existingTellentMemberValidator,
  TellentMembersHttpClient,
  // eslint-disable-next-line boundaries/element-types
} from '@core/tellent-data'; // FIXME
import {
  TellentAccessService,
  TellentAppsDataFacade,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import {
  BRAND,
  InviteMemberPayload,
  MEMBER_LISTED_ONLY,
  SeatKind,
  TellentBrand,
  TellentClientDialogResult,
  TellentOrgAbilities,
  TellentOrgAbilitiesExtended,
} from '@core/tellent-types';
import { resolveMemberFullName, TellentComponentsNotificationService } from '@core/tellent-util';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  DividerComponent,
  FormErrorDirective,
  FormFieldComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
  SelectComponent,
} from '@core/user-interface-legacy';
import { macroTask } from '@core/utils-legacy';

import { EditMemberAdminDialogComponent } from '../edit-member-admin-dialog/edit-member-admin-dialog.component';
import type { SeatType } from './access-roles-table/access-roles-table.component';
import { AccessRolesTableComponent } from './access-roles-table/access-roles-table.component';
import { AccessRolesTableService } from './access-roles-table/access-roles-table.service';
import { AddMemberAnalyticsService } from './add-member-analytics';
import { ExistingMemberInfoComponent } from './existing-member-info/existing-member-info.component';

type FormField<T> = {
  email: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
} & T;

type AddMemberForm = {
  members: FormArray<FormGroup<FormField<{}>>>;
  seats: FormGroup<{
    recruitee: FormGroup<SeatType>;
    kiwi: FormGroup<SeatType>;
  }>;
  sendInvitation: FormControl<boolean | null>;
};

type ProductSeat =
  | Partial<{
      toggle: boolean | null;
      appGuid: string | null;
      roleExternalId: string | null;
    }>
  | undefined;

enum DialogVariant {
  Simplified = 'simplified',
  Complex = 'complex',
}

const removeDuplicateError = (control: AbstractControl<null | string, null | string> | null) => {
  if (control?.errors?.duplicatedEmail) {
    const { duplicatedEmail, ...errors } = control?.errors;
    control?.setErrors(errors && Object.keys(errors).length ? { ...errors } : null);
  }
};

type AddMemberDialogData = any;
type AddMemberDialogResult = TellentClientDialogResult<any>;

@Component({
  selector: 'tlc-add-member-dialog',
  templateUrl: './add-member-dialog.component.html',
  styleUrls: ['./add-member-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    CheckboxComponent,
    ButtonComponent,
    IconComponent,
    LabelComponent,
    IconButtonComponent,
    DividerComponent,
    HeadingComponent,
    TranslatePipe,
    SelectComponent,
    NgTemplateOutlet,
    ButtonLoadingDirective,
    ExistingMemberInfoComponent,
    AccessRolesTableComponent,
    FormErrorDirective,
    ProtectedControlDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddMemberDialogComponent
  implements DialogComponentI<AddMemberDialogData, AddMemberDialogResult>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly analytics = inject(AddMemberAnalyticsService);
  private readonly router = inject(Router);

  private readonly appsDataFacade = inject(TellentAppsDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly accessRolesTableSrv = inject(AccessRolesTableService);

  public readonly brand = inject<TellentBrand>(BRAND);
  public readonly tellentAccessService = inject(TellentAccessService);
  public readonly DialogVariant = DialogVariant;
  public readonly TellentBrand = TellentBrand;

  public webComponentOutput: AddMemberDialogResult;

  public tellentMembersHttpClient = inject(TellentMembersHttpClient);
  public i18n = inject(I18nService);
  public notificationService = inject(TellentComponentsNotificationService);

  public formGroup = this.formBuilder.group<AddMemberForm>({
    sendInvitation: new FormControl(false),
    members: this.formBuilder.array([this.createMemberFormGroup()]),
    seats: this.formBuilder.group({
      recruitee: this.createSeatFormGroup(),
      kiwi: this.createSeatFormGroup(),
    }),
  });

  public get members() {
    return this.formGroup.controls.members.controls;
  }

  public get seats() {
    return this.formGroup.controls.seats.controls;
  }

  public recruiteeSelectOptions = this.appsDataFacade.rolesSelectOptions(SeatKind.Recruitment);
  public kiwiSelectOptions = this.appsDataFacade.rolesSelectOptions(SeatKind.CoreHR);

  public recruiteeApp$ = this.appsDataFacade.appBySeatKind(SeatKind.Recruitment);

  public kiwiApp$ = this.appsDataFacade.appBySeatKind(SeatKind.CoreHR);

  public defaultRecruiteeRole = this.appsDataFacade.defaultRoleBySeatKind(SeatKind.Recruitment);

  public defaultKiwiRole = this.appsDataFacade.defaultRoleBySeatKind(SeatKind.CoreHR);

  public loading$ = signal(false);
  public canAddRow = signal(true);
  public dialogVariant = signal(DialogVariant.Simplified);

  public emailFieldErrors = {
    required: this.i18n.translate('tlc.common.errors.required'),
    email: this.i18n.translate('tlc.common.errors.email'),
    duplicatedEmail: this.i18n.translate('tlc.common.errors.duplicated_email'),
  };

  public canManageTellentUsers = this.tellentAccessService.canManageTellentUsers();

  public setDefaultRoles = effect(() => {
    const defaultRecruiteeRole = this.defaultRecruiteeRole();
    const defaultKiwiRole = this.defaultKiwiRole();

    if (defaultRecruiteeRole || defaultKiwiRole) {
      const defaults = {
        recruitee: defaultRecruiteeRole,
        kiwi: defaultKiwiRole,
      };

      if (this.brand === TellentBrand.Recruitee) {
        if (defaults.recruitee?.externalId) {
          const sendInvitationControl = this.formGroup.controls.sendInvitation;

          this.seats.recruitee.patchValue(
            {
              roleExternalId: defaults.recruitee.externalId,
              toggle: true,
            },
            { emitEvent: false },
          );

          sendInvitationControl.enable({ emitEvent: false });
          sendInvitationControl.setValue(true, { emitEvent: false });
        }
      }

      if (this.brand === TellentBrand.Kiwi) {
        if (defaults.kiwi?.externalId) {
          this.seats.kiwi.patchValue(
            {
              roleExternalId: defaults.kiwi.externalId,
              toggle: true,
            },
            { emitEvent: false },
          );
        }
      }

      macroTask(() => this.formGroup.updateValueAndValidity());
    }
  });

  private editDialogTrackReset$: Subject<void> = new Subject();

  public ngOnInit() {
    this.formGroup.controls.members.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(members => {
        const controls = this.formGroup.controls.members.controls;
        const emails = controls.map(control => control.get('email')?.value);

        if (members.length === 1) {
          const control = this.formGroup.controls.members.controls[0].get('email');
          if (control) removeDuplicateError(control);
        } else {
          controls.forEach(control => {
            const email = control.get('email');

            if (
              emails.some(
                (emailValue, i) => emails.indexOf(emailValue) !== i && emailValue === email?.value,
              )
            ) {
              email?.setErrors({ duplicatedEmail: true });
            } else {
              removeDuplicateError(email);
            }
          });
        }
      });
  }

  public ngAfterViewInit(): void {
    const seats = this.formGroup.controls.seats.controls;
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.Recruitment, seats.recruitee);
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.CoreHR, seats.kiwi);
  }

  public setDialogData(data: any, triggerUpdate: boolean): void {
    this.appsDataFacade.fetchCollection();
    this.dialogVariant.set(this.resolveDialogVariant());
    this.analytics.trackDialogOpened(data?.context || null);
  }

  public addMemberFormGroup() {
    this.formGroup.controls.members.push(this.createMemberFormGroup());
    this.canAddRow.set(this.formGroup.controls.members.length < 50);
    this.cdRef.detectChanges();
  }

  public removeMemberFormGroup(index: number) {
    this.formGroup.controls.members.removeAt(index);
    this.canAddRow.set(this.formGroup.controls.members.length < 50);
    this.cdRef.detectChanges();
  }

  public close(): void {
    this.dialogRef.close();
  }

  public submit(): void {
    if (this.shouldOpenSecondStep()) {
      const payload = this.prepareActionPayload();
      const seats = this.formGroup.value.seats;

      this.formGroup.markAsPristine();
      this.dialogRef.close({
        displayAutj: true,
        shouldOpenSecondStep: true,
        recruiteeRoleId: this.getRecruiteeRoleIdIfExists(seats?.recruitee),
        kiwiRoleId: this.getKiwiRoleIdIfExists(seats?.kiwi),
        ...payload,
      });
    } else {
      this.submitMembersWithoutSecondStep();
    }
  }

  public editAccess(memberGuid: string) {
    this.editDialogTrackReset$.next();
    const ref: DialogRef<EditMemberAdminDialogComponent> = this.dialogService.openCustom(
      EditMemberAdminDialogComponent,
      {
        width: DialogWidth.Medium,
        closeGuard: () => ref.componentInstance.protectedControls(() => true),
      },
      { memberId: memberGuid },
    );

    this.trackUpdateSuccess(memberGuid);
  }

  private trackSubmitSuccess(
    addQuantity: number,
    sendInvitation: boolean,
    recruiteeSeat: false | string,
    kiwiSeat: false | string,
  ): void {
    this.analytics.trackSent(addQuantity, sendInvitation, recruiteeSeat, kiwiSeat);
  }

  private createMemberFormGroup() {
    return this.formBuilder.group({
      email: [
        '',
        [Validators.required, emailValidator],
        [
          this.tellentAccessService.canManageTellentUsers()
            ? existingTellentMemberValidator(this.tellentMembersHttpClient)
            : existingAppMemberValidator(this.tellentMembersHttpClient),
        ],
      ],
      firstName: ['', [noWhitespaceValidator]],
      lastName: ['', [noWhitespaceValidator]],
    });
  }

  private createSeatFormGroup(): FormGroup<SeatType> {
    return this.formBuilder.group<SeatType>({
      toggle: new FormControl<boolean | null>(null),
      appGuid: new FormControl<string | null>(''),
      roleExternalId: new FormControl<string | null>(''),
    });
  }

  private resolveDialogVariant(): DialogVariant {
    return this.canManageTellentUsers ? DialogVariant.Complex : DialogVariant.Simplified;
  }

  private trackUpdateSuccess(memberGuid: string) {
    this.membersDataFacade.manageSeatsSuccess$
      .pipe(take(1), takeUntil(this.editDialogTrackReset$), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.membersDataFacade.fetchOne(memberGuid);
      });
  }

  private handleSubmitSuccess(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberSuccess$,
      this.membersDataFacade.inviteAppMemberSuccess$,
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ payload }) => {
        this.formGroup.markAsPristine();

        this.loading$.set(false);
        const { firstName, lastName, email } = this.formGroup.controls.members.controls[0].value;
        this.notificationService.success(
          this.i18n.translate('tlc.members.add_member.success_toast', {
            count: this.formGroup.controls.members.length,
            fullName: resolveMemberFullName({
              firstName: firstName || '',
              lastName: lastName || '',
              email: email || '',
            }),
          }),
        );

        this.dialogRef.close({ success: true, payload });
      });
  }

  private handleSubmitFailure(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberFail$,
      this.membersDataFacade.inviteAppMemberFail$,
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loading$.set(false));
  }

  private submitMembersWithoutSecondStep(): void {
    this.loading$.set(true);

    const { members, seats, sendInvitation } = this.formGroup.value;

    const kiwiSeat = seats?.kiwi;
    const recruiteeSeat = seats?.recruitee;

    const actionPayload = this.prepareActionPayload();

    if (this.tellentAccessService.canManageTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(actionPayload.payload);
    } else {
      this.membersDataFacade.inviteAppMember(actionPayload.payload);
    }

    this.trackSubmitSuccess(
      members?.length!,
      !sendInvitation,
      this.getRecruiteeRoleIdIfExists(recruiteeSeat),
      this.getKiwiRoleIdIfExists(kiwiSeat),
    );
    this.handleSubmitSuccess();
    this.handleSubmitFailure();
  }

  private shouldOpenSecondStep(): boolean {
    const selectedRoleExternalId = this.seats.recruitee.value.roleExternalId;
    if (selectedRoleExternalId && this.recruiteeApp$()) {
      const selectedRoleAbilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended> =
        this.recruiteeApp$()!.roles.find(
          role => role.externalId === selectedRoleExternalId,
        )!.abilities!;
      return !selectedRoleAbilities.some(
        ability => ability === TellentOrgAbilitiesExtended.RecruitmentViewAllJobs,
      );
    }
    return false;
  }

  private prepareActionPayload(): { payload: InviteMemberPayload } {
    const { members, seats, sendInvitation } = this.formGroup.value;

    const kiwiSeat = seats?.kiwi;
    const recruiteeSeat = seats?.recruitee;

    const hasMemberListedOnlyAbility = kiwiSeat?.roleExternalId === MEMBER_LISTED_ONLY;

    const payloadSeats = [
      {
        includeInPayload: kiwiSeat?.toggle || hasMemberListedOnlyAbility,
        access: kiwiSeat?.toggle && !hasMemberListedOnlyAbility,
        appGuid: this.kiwiApp$()?.guid,
        roleExternalId: !hasMemberListedOnlyAbility ? kiwiSeat?.roleExternalId : null,
      },
      {
        includeInPayload: recruiteeSeat?.toggle,
        access: recruiteeSeat?.toggle,
        appGuid: this.recruiteeApp$()?.guid,
        roleExternalId: recruiteeSeat?.roleExternalId,
      },
    ]
      .filter(seat => seat.appGuid && seat.includeInPayload)
      .map(seat => ({
        access: seat.access,
        appGuid: seat.appGuid,
        roleExternalId: seat.roleExternalId,
      }));

    return {
      payload: <InviteMemberPayload>{
        members,
        seats: payloadSeats,
        sendInvitation: !!sendInvitation,
      },
    };
  }

  private getRecruiteeRoleIdIfExists(recruiteeSeat: ProductSeat): string | false {
    return recruiteeSeat?.toggle
      ? this.recruiteeSelectOptions().find(role => role.value === recruiteeSeat?.roleExternalId!)
          ?.value!
      : false;
  }

  private getKiwiRoleIdIfExists(kiwiSeat: ProductSeat): string | false {
    return kiwiSeat?.toggle
      ? this.kiwiSelectOptions().find(role => role.value === kiwiSeat?.roleExternalId!)?.value!
      : false;
  }
}
